import { Box } from "@mui/material";
import {
  amber,
  indigo,
  lightBlue,
  green,
  grey,
  pink,
  red,
  deepPurple,
  deepOrange,
  lightGreen,
  lime,
  blueGrey,
  teal,
  orange,
  brown,
  cyan,
  blue,
  purple,
} from "@mui/material/colors";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";

import type { SalesVertical } from "@xop/dashboard-shared";

import type { SxStyle } from "../leads/heatStyles.js";

/**
 * One-glance pill identifying which XOP vertical a lead belongs to.
 * Order + colours are deliberate: the sixteen PRIMARY critical-infrastructure
 * verticals the agent centres on — ENERGY fully split into nuclear / oil / gas
 * / wind / power (retarget 2026-06-21), AIRPORTS split domestic/international
 * and MILITARY BASES split ANG / US / allied (retarget 2026-08-30), plus
 * chemical, hospitals, VA homes, fire/public-safety, prisons and private-wire
 * carriers — get strong distinct colours; the light-tail verticals keep theirs;
 * the two LEGACY lumped slugs (oil_and_gas / power_generation) are FOLDED onto
 * their granular chip (Oil / Power & Utilities) so a not-yet-swept lead never
 * shows a combined "Oil & Gas" / "Nuclear / Power" pill — the agent's hygiene
 * sweep re-splits the DB row onto the right sub-sector shortly after;
 * "unknown" is muted. Lockstep with the agent's `services.verticals` roster.
 *
 * Colours are grouped by FAMILY, distinguished by weight: the airport chips are
 * blues, the three base types blueGrey/indigo/grey, corrections sits with
 * government's brown and VA homes with healthcare's teal. The residual PARENTS
 * of the 2026-08-30 split (`aviation`, `defence_military`) take the LIGHTEST
 * weight of their family — visibly the fallback, but never the muted
 * "Unclassified" grey, because a lead on one IS classified.
 *
 * A slug OFF that roster is not automatically unclassified: an operator-defined
 * or steer-derived harvest campaign declares its own verticals
 * (`wired_networks`, `wallstreet_trading`), and those are exactly as
 * deliberate as the roster ones. They render as a humanized neutral chip.
 * Only nothing / the explicit `unknown` sentinel reads "Unclassified".
 */
// `SalesVertical` is a free-form `string` since the v4 senior-manager
// rebuild, so `META` is a string-index map: every lookup (and the
// `unknown` slot itself) is `T | undefined` under
// `noUncheckedIndexedAccess`. `UNKNOWN` is a standalone definite
// value so the `?? UNKNOWN` fallback resolves to a non-optional type.
interface VerticalMeta {
  label: string;
  /** Pastel tint background + saturated text, per vertical identity. */
  bg: string;
  fg: string;
  /**
   * No palette colour of its own — render the neutral chip. True for a
   * harvest campaign's self-declared slug, which is a real classification
   * but isn't on the roster the palette is keyed to.
   */
  neutral?: boolean;
}

const UNKNOWN: VerticalMeta = {
  label: "Unclassified",
  // Mapped onto theme tokens via a sentinel; resolved in `tintSx`.
  bg: "surfaceSoft",
  fg: "text.disabled",
};

// Each chromatic vertical reuses the MUI named-colour scales (no raw
// hex) so the look matches the prior `bg-sky-500/12 text-sky-700` pills
// exactly: a faint tinted fill (alpha 0.12) + the 700-weight ink.
const META: Record<SalesVertical, VerticalMeta> = {
  // ── Energy, fully split (the 2026-06-21 retarget) — each sub-sector
  //    its own chip so a nuclear plant reads differently from a wind farm. ──
  nuclear:              { label: "Nuclear",            bg: green[700],     fg: green[800] },
  oil:                  { label: "Oil",                bg: amber[800],     fg: amber[900] },
  gas:                  { label: "Gas / LNG",          bg: deepOrange[600], fg: deepOrange[800] },
  wind:                 { label: "Wind & Renewables",  bg: lightGreen[700], fg: lightGreen[900] },
  power:                { label: "Power & Utilities",  bg: lime[800],      fg: lime[900] },
  // ── Airports, split domestic vs international (the 2026-08-30 retarget) ──
  airports_domestic:    { label: "Domestic Airports", bg: lightBlue[500], fg: lightBlue[800] },
  airports_intl:        { label: "Int'l Airports", bg: cyan[800],  fg: cyan[900] },
  // ── Other critical-infrastructure / life-safety verticals ──
  chemical:             { label: "Chemical",            bg: deepPurple[400], fg: deepPurple[700] },
  healthcare:           { label: "Hospitals / Healthcare", bg: teal[500],   fg: teal[800] },
  va_homes:             { label: "VA Homes",            bg: teal[800],      fg: teal[900] },
  public_safety:        { label: "Public Safety",      bg: red[500],       fg: red[800] },
  corrections:          { label: "Prisons / Corrections", bg: brown[700],   fg: brown[900] },
  // ── Military bases, split three ways (the 2026-08-30 retarget) — the ANG
  //    is a different buyer from the USAF, and an allied base a different
  //    sale again, so each gets its own chip. ──
  air_national_guard:   { label: "Air National Guard",  bg: indigo[700],    fg: indigo[900] },
  military_bases_us:    { label: "USAF & Naval Bases", bg: blueGrey[600], fg: blueGrey[900] },
  air_force_bases_intl: { label: "Int'l Air Force Bases", bg: grey[600], fg: grey[800] },
  private_wire_carrier: { label: "Private Wire Carriers", bg: purple[700],  fg: purple[900] },
  hospitality:          { label: "Hotels / Hospitality",  bg: orange[500],  fg: orange[900] },
  // ── The RESIDUAL PARENTS of the 2026-08-30 split — an AIRLINE is Aviation,
  //    an AIRPORT is one of the two chips above; a defence MINISTRY is
  //    Defence / Military, a BASE is one of the three above. Lightest weight
  //    of their family: still a real chip, visibly the fallback. ──
  aviation:             { label: "Aviation",            bg: lightBlue[300], fg: lightBlue[700] },
  defence_military:     { label: "Defence / Military",  bg: blueGrey[300],  fg: blueGrey[800] },
  // ── Light-tail verticals (kept for breadth) ──
  // Keep this set in lockstep with the agent's `services.verticals`
  // SECONDARY_VERTICALS roster — a slug the agent classifies but the badge
  // doesn't know falls back to "Unclassified" even though it IS classified.
  fintech_trader_voice: { label: "Trader Voice",  bg: pink[500],      fg: pink[800] },
  government:           { label: "Government",     bg: brown[500],     fg: brown[800] },
  education:            { label: "Education",      bg: cyan[600],      fg: cyan[900] },
  transportation:       { label: "Transportation", bg: blue[600],     fg: blue[800] },
  telco_carrier:        { label: "Telco / Carrier", bg: purple[400],  fg: purple[700] },
  corporate:            { label: "Corporate",     bg: indigo[500],    fg: indigo[800] },
  // ── LEGACY lumped slugs (pre-2026-06-21) — FOLDED onto their granular chip
  //    so a not-yet-swept lead never renders a combined "Oil & Gas" /
  //    "Nuclear / Power" pill (the operator's 2026-06-27 ask: "one oil, one
  //    gas, one nuclear"). They keep a real chip (not "Unclassified") during
  //    the brief window before the agent's hygiene sweep RE-SPLITS the DB row
  //    onto the right granular sub-sector (oil-vs-gas, nuclear-vs-wind-vs-power
  //    by org identity) — see `services.verticals.resplit_legacy_lumped`. The
  //    lumped value defaults to the broad bucket (oil / power) until then. ──
  oil_and_gas:          { label: "Oil",                bg: amber[800],     fg: amber[900] },
  power_generation:     { label: "Power & Utilities",  bg: lime[800],      fg: lime[900] },
  unknown:              UNKNOWN,
};

/**
 * A slug that isn't on the shared roster but ISN'T the `unknown` sentinel
 * either — i.e. a vertical an operator-defined or steer-derived harvest
 * campaign DECLARED for itself (`wired_networks`, `wallstreet_trading`).
 *
 * Those are real classifications: the campaign definition is what says what
 * the sector is. Rendering them as "Unclassified" (the old `?? UNKNOWN`
 * fallback) told the operator their own campaign wasn't working — which on
 * 2026-08-04 was indistinguishable from the agent bug that WAS quarantining
 * those rows. Humanize the slug instead, and keep "Unclassified" for the one
 * case that means it: nothing / the explicit `unknown` sentinel.
 */
function campaignMeta(slug: string): VerticalMeta | null {
  const raw = (slug ?? "").trim();
  if (!raw || raw === "unknown") return null;
  const label = raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  if (!label) return null;
  // No colour is assigned to a campaign's own slug (the palette is keyed to
  // the standing roster), so it takes the neutral chip — but with readable
  // ink, not the disabled grey that marks a row we failed to classify.
  return { label, bg: "", fg: "text.secondary", neutral: true };
}

function metaFor(vertical: SalesVertical): VerticalMeta {
  return META[vertical] ?? campaignMeta(vertical) ?? UNKNOWN;
}

/** Build the pill tint sx from a meta entry (handles the neutral variants). */
function tintSx(m: VerticalMeta): SxStyle {
  if (m === UNKNOWN) {
    return { bgcolor: "surfaceSoft", color: "text.disabled" };
  }
  if (m.neutral) {
    return { bgcolor: "surfaceSoft", color: m.fg };
  }
  return {
    bgcolor: alpha(m.bg, 0.12),
    color: m.fg,
  };
}

const baseSx: SxStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  px: 1,
  py: 0.25,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  lineHeight: 1.6,
  // Keep multi-word labels ("Nuclear / Power", "Hospitals / Healthcare") on a
  // single line — never let the pill break onto two rows inside its column.
  // The Vertical column in `LeadsTable` is sized to fit the longest label.
  whiteSpace: "nowrap",
};

export function VerticalBadge({
  vertical,
  sx,
}: {
  vertical: SalesVertical;
  sx?: SxProps<Theme>;
}): JSX.Element {
  const m = metaFor(vertical);
  return (
    <Box
      component="span"
      sx={[baseSx, tintSx(m), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {m.label}
    </Box>
  );
}

export function verticalLabel(v: SalesVertical): string {
  return metaFor(v).label;
}

/**
 * The selectable vertical options, in display order: the sixteen PRIMARY
 * critical-infrastructure verticals (energy split 2026-06-21 — nuclear / oil /
 * gas / wind / power; airports + military bases split 2026-08-30) first, then
 * the light-tail verticals, then `unknown`. Shared by the Leads **filter**
 * dropdown (`LeadFilters`) and the inline **classification editor** on the
 * leads list (`LeadsTable`), so the two speak one vocabulary. The two LEGACY
 * lumped slugs (`oil_and_gas` / `power_generation`) still RENDER a chip but are
 * deliberately NOT offered here — new classification uses the granular slugs.
 * The residual parents ARE offered (an airline / a defence ministry is a real
 * answer), just after the granular options they defer to.
 *
 * Mirrors `VERTICALS` in `@xop/dashboard-shared` — same order, same set.
 */
export const SALES_VERTICALS: readonly SalesVertical[] = [
  "airports_domestic",
  "airports_intl",
  "nuclear",
  "oil",
  "gas",
  "wind",
  "power",
  "chemical",
  "healthcare",
  "va_homes",
  "public_safety",
  "corrections",
  "air_national_guard",
  "military_bases_us",
  "air_force_bases_intl",
  "private_wire_carrier",
  "aviation",
  "defence_military",
  "hospitality",
  "fintech_trader_voice",
  "government",
  "education",
  "transportation",
  "telco_carrier",
  "corporate",
  "unknown",
];
